import { LegalAcceptanceSummary } from './legal-compliance.types';

export enum ServiceProviderStatus {
  PENDING = 0,
  ACTIVE = 1,
  ARCHIVED = 2,
  SUSPENDED = 3,
  REJECTED = 4,
}

export enum BusinessType {
  COOPERATIVE = 1,
  SOLE_TRADER = 2,
  GENERAL_PARTNERSHIP = 3,
  LIMITED_PARTNERSHIP = 4,
  LIMITED_COMPANY = 5,
}

export interface Service {
  id?: string;
  service_provider_id?: string;
  service_provider_name?: string;
  name: string;
  status?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ServicePayload {
  id?: string; // Present for existing services
  name: string; // Always required (for both new and existing)
}

export interface Harbor {
  id: string;
  name: string;
  marina_id?: string;
  description?: string;
  logo?: string | null;
  location?: string;
  latitude?: string;
  longitude?: string;
  berth_capacity?: number | null;
  depth?: number | null;
  max_depth?: number | null;
  min_depth?: number | null;
  depth_unit?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  category?: string;
  business_hours?: string | null;
  facilities?: string[];
  amenities?: string[];
  mooring_info?: string | null;
  media?: string | null;
  website_url?: string | null;
  is_deleted?: boolean;
  status?: number;
  harbor_status?: number;
  featured?: boolean;
  is_approved?: boolean;
  country_code?: string;
  country_short_code?: string;
  fairway_map?: string | null;
  vhf_channel?: string | null;
  minimum_advance_booking_days?: number;
  club_id?: string | null;
  send_booking_to_admin?: boolean;
  booking_enabled?: boolean;
  whatsapp_link?: string | null;
  facebook_link?: string | null;
  instagram_link?: string | null;
  pdf_link?: string | null;
  admin_notes?: string | null;
  history_log?: string | null;
  primary_language_spoken?: string | null;
  mooring_style?: string | null;
  coastal_area?: string | null;
  deletion_requested_by?: string | null;
  deletion_requested_at?: string | null;
  deletion_reason?: string | null;
  translations?: Record<string, unknown> | null;
  seasonality_support?: boolean;
  seasonal_dates?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LinkedHarbor {
  id: string;
  harbor_id: string;
  latitude: string;
  longitude: string;
  harbor: Harbor;
}

export interface ServiceProviderResponse {
  id: string;
  legal_name: string;
  display_name: string;
  vat_id: string;
  contact_email: string;
  contact_phone: string;
  country_code: string;
  country_short_code: string;
  contact_phone_country_short_code?: string;
  website: string;
  street: string | null;
  city: string | null;
  post_code: string | null;
  country: string;
  business_type: BusinessType;
  category: string;
  description: string;
  latitude: string;
  longitude: string;
  image: string | null;
  public_visibility: boolean;
  status: ServiceProviderStatus;
  services: Service[];
  linked_harbors: LinkedHarbor[];
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
  user?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    roles: Array<{
      id: string;
      name: string;
      display_name: string;
      description?: string;
      status?: number;
      created_at?: string;
      updated_at?: string;
      has_admin_panel_access?: boolean;
      has_user_front_access?: boolean;
    }>;
  };
  legal_acceptance_summary?: LegalAcceptanceSummary | null;
}

export interface ServiceProviderListResponse {
  status: boolean;
  data: ServiceProviderResponse[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface ServiceProviderDetailResponse {
  data: ServiceProviderResponse;
}

export interface ServiceProviderListQueryParams {
  search?: string;
  status?: ServiceProviderStatus;
  country?: string;
  sort_by?: string;
  sort_order?: "ASC" | "DESC";
  page?: number;
  limit?: number;
  exclude_pending?: boolean;
}

export interface CreateServiceProviderPayload {
  legal_name?: string;
  display_name?: string;
  vat_id?: string;
  contact_email?: string;
  phone?: string;
  country_code?: string;
  country_short_code?: string;
  contact_phone_country_short_code?: string;
  website?: string;
  business_type?: BusinessType;
  description?: string;
  street?: string;
  city?: string;
  post_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  harbor_ids?: string[];
}

export interface UpdateServiceProviderPayload {
  legal_name?: string;
  display_name?: string;
  vat_id?: string;
  contact_email?: string;
  phone?: string;
  country_code?: string;
  country_short_code?: string;
  contact_phone_country_short_code?: string;
  website?: string;
  business_type?: BusinessType;
  description?: string;
  street?: string;
  city?: string;
  post_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  harbor_ids?: string[];
}

export interface ChangeServiceProviderStatusPayload {
  status: ServiceProviderStatus;
  reason?: string;
}

export interface ServiceProviderFormData {
  id?: string;
  legal_name?: string;
  display_name?: string;
  contact_email?: string;
  contact_phone?: string;
  country_code?: string;
  country_short_code?: string;
  contact_phone_country_short_code?: string;
  website?: string;
  service_category?: string;
  description?: string;
  street?: string;
  city?: string;
  post_code?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
  vat_id?: string;
  business_type?: BusinessType;
  status?: ServiceProviderStatus;
  linked_harbor_ids?: string[];
  public_visibility?: boolean;
  image?: string | null;
  services?: Service[];
}
